export interface BrandMarkProps {
  /** Big brand text — e.g. "NUCCE" or "NU Assist". Rendered in accent color. */
  brand: string;
  /** Small uppercase caption beneath the brand — e.g. "NU Advisory Partners". */
  caption: string;
}

/**
 * BrandMark — logo box + brand text + uppercase caption.
 *
 * Visual spec from `.login-logo-wrap` / `.login-logo-box` / `.login-brand`
 * in nucce_extension_preview_v17.html (view 0).
 *
 * Tokenized: brand wordmark `text-xl font-bold` (20px/700) → `text-h1`
 * (the role already bundles 700 weight); the 10px caption →
 * `text-micro` with `font-normal` overriding the role's default 600
 * back to v17's unweighted caption. `rounded-token` (12px) unchanged —
 * it's the retained alias of `rounded-lg` / `--radius`.
 *
 * The glyph is `fill-bg`, the same pairing every solid-accent surface uses
 * for its label (`bg-accent text-bg`). It was a literal `#0a0a0a`, which is
 * `--bg` on dark and therefore looked tokenized; on light `bg-accent` is a
 * deep teal and the near-black glyph sat on it at about 3:1. The contrast
 * suite measures tokens, so a literal is invisible to it.
 */
export function BrandMark({ brand, caption }: BrandMarkProps) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 bg-accent rounded-token mb-2.5">
        <svg
          viewBox="0 0 24 24"
          className="w-7 h-7 fill-bg"
          aria-hidden="true"
          focusable="false"
        >
          <title>{brand} logo</title>
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      </div>
      <div className="text-h1 text-accent tracking-[1px]">
        {brand}
      </div>
      <div className="text-micro font-normal text-text-muted uppercase tracking-[1px] mt-0.5">
        {caption}
      </div>
    </div>
  );
}
